/**
 * The agent's live "now" panel for Workforce Management.
 *
 * Everything time-based ticks every second on the client:
 *   - the current scheduled activity with a countdown and an animated
 *     progress bar (moving stripes while working, a slow breathing fill on
 *     a break, red stripes when out of adherence),
 *   - the whole shift's progress with a live "now" marker,
 *   - how long the agent has been logged in and in their current status,
 *   - punctuality for the shift (early / on time / late / not logged in),
 *   - suggestions derived from schedule × live status.
 *
 * The live status comes from the socket-fed agentStore, so switching to a
 * break or back to Available repaints instantly; the server snapshot
 * (/wfm/me/adherence) is refreshed every 30 s and right after each status
 * change so the adherence figures and exceptions stay authoritative.
 */
import { useEffect, useMemo, useState, type JSX } from 'react'
import {
  AlarmClock, CheckCircle2, Clock3, Coffee, Info, LogIn, Sparkles, TimerReset, TriangleAlert, Utensils, Users, Briefcase,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { useAgentStore } from '../../store/agentStore.js'
import { CARD } from './WfmUi.js'
import { fmtSpan, fmtTime } from './wfm-shared.js'
import { usePhoneCall, useWfmLiveFeed, type LiveBlock, type MeAdherence } from './wfm-live-store.js'

export type { LiveBlock, MeAdherence } from './wfm-live-store.js'

// ---------------------------------------------------------------------------
// Data + clock
// ---------------------------------------------------------------------------

export function useNow(ms = 1000): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), ms)
    return () => clearInterval(t)
  }, [ms])
  return now
}

/** 1:05:09 / 04:12 */
export function clock(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000))
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  const mm = String(m).padStart(2, '0')
  const ss = String(s).padStart(2, '0')
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`
}

/** 2h 05m / 12m */
function dur(ms: number): string {
  const min = Math.max(0, Math.round(ms / 60_000))
  const h = Math.floor(min / 60)
  return h > 0 ? `${h}h ${String(min % 60).padStart(2, '0')}m` : `${min}m`
}

const STATUS_LABEL: Record<string, string> = {
  OFFLINE: 'Logged out', REGISTERED: 'Logged in', IDLE: 'Available', RESERVING: 'Reserving', RESERVED: 'Reserved',
  INVITING: 'Offering', RINGING: 'Ringing', ANSWERED: 'On a call', IN_CALL: 'On a call', PAUSED: 'Paused',
  ON_BREAK: 'On break', ACW: 'Wrap-up', AVAILABLE_ON_DEMAND: 'On demand',
}
const statusLabel = (s: string): string => STATUS_LABEL[s] ?? s.replace(/_/g, ' ').toLowerCase()

const CATEGORY_ICON: Record<string, LucideIcon> = {
  WORK: Briefcase, BREAK: Coffee, LUNCH: Utensils, MEETING: Users, TRAINING: Sparkles, OFF: Clock3, OTHER: Clock3,
}

export type BarMode = 'work' | 'rest' | 'meeting' | 'out' | 'idle'

export function barMode(category: string | undefined, inAdherence: boolean | null): BarMode {
  if (inAdherence === false) return 'out'
  if (!category) return 'idle'
  if (category === 'WORK') return 'work'
  if (category === 'BREAK' || category === 'LUNCH') return 'rest'
  return 'meeting'
}

const BAR_STYLE: Record<BarMode, { fill: string; anim: string; track: string }> = {
  work: {
    fill: 'linear-gradient(90deg,#3366FF,#4F7BFF)',
    anim: 'wfm-stripes 1.1s linear infinite',
    track: '#E8EEFF',
  },
  rest: {
    fill: 'linear-gradient(90deg,#F59E0B,#FBBF24)',
    anim: 'wfm-breathe 2.8s ease-in-out infinite',
    track: '#FEF3C7',
  },
  meeting: {
    fill: 'linear-gradient(90deg,#7C3AED,#8B5CF6)',
    anim: 'wfm-stripes 1.8s linear infinite',
    track: '#EDE9FE',
  },
  out: {
    fill: 'linear-gradient(90deg,#E11D48,#F43F5E)',
    anim: 'wfm-stripes 0.7s linear infinite',
    track: '#FFE4E6',
  },
  idle: { fill: '#CBD5E1', anim: 'none', track: '#F1F5F9' },
}

export const LIVE_KEYFRAMES = `
@keyframes wfm-stripes { from { background-position: 0 0 } to { background-position: 28px 0 } }
@keyframes wfm-breathe { 0%,100% { opacity: 1 } 50% { opacity: .55 } }
@keyframes wfm-pulse-dot { 0% { box-shadow: 0 0 0 0 var(--c) } 70% { box-shadow: 0 0 0 7px transparent } 100% { box-shadow: 0 0 0 0 transparent } }
@media (prefers-reduced-motion: reduce) { .wfm-anim { animation: none !important } }
`

/** Animated progress bar; `pct` 0–100. */
export function LiveBar({ pct, mode, height = 10 }: { pct: number; mode: BarMode; height?: number }): JSX.Element {
  const st = BAR_STYLE[mode]
  const striped = mode === 'work' || mode === 'meeting' || mode === 'out'
  return (
    <div className="relative w-full overflow-hidden rounded-[3px]" style={{ height, background: st.track }}>
      <div
        className="wfm-anim absolute inset-y-0 left-0 rounded-[3px] transition-[width] duration-1000 ease-linear"
        style={{
          width: `${Math.max(0, Math.min(100, pct))}%`,
          backgroundImage: striped
            ? `repeating-linear-gradient(135deg,rgba(255,255,255,.22) 0 7px,transparent 7px 14px),${st.fill}`
            : st.fill,
          backgroundSize: striped ? '28px 100%, 100% 100%' : undefined,
          animation: st.anim,
        }}
      />
    </div>
  )
}

function LiveDot({ color }: { color: string }): JSX.Element {
  return (
    <span
      className="wfm-anim inline-block h-2 w-2 rounded-full"
      style={{ background: color, ['--c' as string]: `${color}66`, animation: 'wfm-pulse-dot 1.8s ease-out infinite' }}
    />
  )
}

// ---------------------------------------------------------------------------
// Suggestions
// ---------------------------------------------------------------------------

interface Tip { id: string; tone: 'good' | 'info' | 'warn' | 'bad'; Icon: LucideIcon; text: string }

function suggestions(d: MeAdherence, live: string, current: LiveBlock | null, next: LiveBlock | null, inAdh: boolean | null, now: number, tz: string, deferring: boolean): Tip[] {
  const tips: Tip[] = []
  if (deferring && current) {
    tips.push({ id: 'defer', tone: 'info', Icon: Coffee, text: `Your ${current.activity.toLowerCase()} is on hold while you're on this call — you'll still get the full ${dur(blockLength(current))} when it ends.` })
  } else if (current?.scheduledStart && current.category !== 'WORK' && (current.deferredMin ?? 0) > 0) {
    tips.push({ id: 'deferred', tone: 'good', Icon: Coffee, text: `Your ${current.activity.toLowerCase()} was moved ${current.deferredMin} min later because of a call — full time kept, until ${fmtTime(current.end, tz)}.` })
  }
  const shiftStart = d.shift ? Date.parse(d.shift.start) : null
  const shiftEnd = d.shift ? Date.parse(d.shift.end) : null
  const onShift = shiftStart !== null && shiftEnd !== null && now >= shiftStart && now < shiftEnd
  const working = !['OFFLINE', 'PAUSED', 'ON_BREAK'].includes(live)

  if (onShift && live === 'OFFLINE') {
    tips.push({ id: 'login', tone: 'bad', Icon: LogIn, text: `Your shift started ${dur(now - shiftStart!)} ago — log in and set yourself Available.` })
  } else if (current && inAdh === false) {
    if (current.category === 'WORK') {
      tips.push({ id: 'back', tone: 'bad', Icon: TriangleAlert, text: `You're scheduled to be working — switch to Available to get back in adherence.` })
    } else if (current.category === 'BREAK' || current.category === 'LUNCH') {
      tips.push({ id: 'take', tone: 'warn', Icon: Coffee, text: `Your ${current.activity.toLowerCase()} started ${dur(now - Date.parse(current.start))} ago — take it now so it isn't lost.` })
    } else {
      tips.push({ id: 'act', tone: 'warn', Icon: TriangleAlert, text: `${current.activity} is scheduled now — set the matching status.` })
    }
  }
  if (current && current.category !== 'WORK' && inAdh !== false) {
    const left = Date.parse(current.end) - now
    if (left <= 3 * 60_000) tips.push({ id: 'ending', tone: 'warn', Icon: TimerReset, text: `${current.activity} ends in ${clock(left)} — be ready to go Available.` })
  }
  if (next) {
    const until = Date.parse(next.start) - now
    if (until <= 10 * 60_000 && until > 0) {
      tips.push({ id: 'next', tone: 'info', Icon: AlarmClock, text: `${next.activity} at ${fmtTime(next.start, tz)} (in ${clock(until)})${next.category !== 'WORK' && working ? ' — wrap up your current task.' : '.'}` })
    }
  }
  if (onShift && shiftEnd! - now <= 15 * 60_000) tips.push({ id: 'end', tone: 'info', Icon: Clock3, text: `Your shift ends at ${fmtTime(d.shift!.end, tz)} — ${clock(shiftEnd! - now)} to go.` })
  if (d.punctuality?.status === 'late') tips.push({ id: 'late', tone: 'warn', Icon: AlarmClock, text: `You logged in ${fmtSpan(d.punctuality.minutes * 60_000)} late today${d.punctuality.at ? ` (at ${fmtTime(d.punctuality.at, tz)}, shift started ${d.shift ? fmtTime(d.shift.start, tz) : ''})` : ''}. Staying on schedule for the rest of the shift lifts your score.` })
  if (d.today?.adherencePct != null && d.today.adherencePct < d.targetPct && d.today.scheduledMin >= 30) {
    tips.push({ id: 'score', tone: 'info', Icon: Info, text: `Adherence so far is ${d.today.adherencePct}% against a ${d.targetPct}% target.` })
  }
  if (tips.length === 0) {
    tips.push(onShift
      ? { id: 'ok', tone: 'good', Icon: CheckCircle2, text: 'You’re on schedule. Nice work — keep it up.' }
      : { id: 'off', tone: 'info', Icon: Clock3, text: d.nextShift ? `Off shift. Next shift starts ${new Date(d.nextShift.startAt).toLocaleString('en-US', { timeZone: tz, weekday: 'short', hour: '2-digit', minute: '2-digit' })}.` : 'You have no upcoming published shifts.' })
  }
  return tips
}

/** Contact statuses (mirror WFM_CONTACT_STATES in @kc3/types). */
export const CONTACT_STATES = new Set(['RESERVING', 'RESERVED', 'INVITING', 'RINGING', 'ANSWERED', 'IN_CALL', 'ACW'])
export const isBreakCategory = (c: string | undefined): boolean => c === 'BREAK' || c === 'LUNCH'
export const blockLength = (b: { start: string; end: string; scheduledStart?: string }): number => Date.parse(b.end) - Date.parse(b.start)

/** True while a due break is waiting for the agent's current contact to end. */
export function isDeferring(b: LiveBlock | null, live: string, now: number): boolean {
  return !!b && isBreakCategory(b.category) && CONTACT_STATES.has(live) && Date.parse(b.start) <= now
}

const TIP_CLS: Record<Tip['tone'], string> = {
  good: 'bg-emerald-50 text-emerald-800 ring-emerald-200',
  info: 'bg-slate-50 text-slate-700 ring-slate-200',
  warn: 'bg-amber-50 text-amber-800 ring-amber-200',
  bad: 'bg-rose-50 text-rose-800 ring-rose-200',
}

// ---------------------------------------------------------------------------
// Widget
// ---------------------------------------------------------------------------

export default function AdherenceWidget({ tz }: { tz: string }): JSX.Element | null {
  const { data, error } = useWfmLiveFeed(tz)
  const now = useNow(1000)
  const agentStatus = useAgentStore((s) => s.status)
  const agentSince = useAgentStore((s) => s.since)
  // A softphone call counts as "on a call" even though the server status stays IDLE.
  const phoneSince = usePhoneCall((s) => s.since)
  const liveStatus = phoneSince !== null ? 'IN_CALL' : agentStatus
  const liveSince = phoneSince !== null ? new Date(phoneSince).toISOString() : agentSince

  const blocks = useMemo(() => data?.today?.blocks ?? [], [data])
  const current = blocks.find((b) => Date.parse(b.start) <= now && now < Date.parse(b.end)) ?? null
  const next = blocks.find((b) => Date.parse(b.start) > now) ?? null
  const live = (liveStatus ?? data?.currentState ?? 'OFFLINE').toUpperCase()
  // A break that falls due while the agent is on a contact is deferred, not
  // missed: it starts when the contact ends and keeps its full length.
  const deferring = isDeferring(current, live, now)
  const inAdh = current ? deferring || current.allowedStates.includes(live) : null
  const sinceIso = liveStatus ? liveSince : data?.stateSince ?? null

  if (error) return null
  if (!data) return <div className={`${CARD} h-[220px] animate-pulse`} />

  const shiftStart = data.shift ? Date.parse(data.shift.start) : null
  const shiftEnd = data.shift ? Date.parse(data.shift.end) : null
  const shiftPct = shiftStart && shiftEnd ? ((now - shiftStart) / (shiftEnd - shiftStart)) * 100 : 0
  const blockPct = current ? ((now - Date.parse(current.start)) / (Date.parse(current.end) - Date.parse(current.start))) * 100 : 0
  const mode = barMode(current?.category, inAdh)
  const CatIcon = CATEGORY_ICON[current?.category ?? 'OTHER'] ?? Clock3
  const tips = suggestions(data, live, current, next, inAdh, now, tz, deferring)
  const statusColor = inAdh === false ? '#F43F5E' : live === 'OFFLINE' ? '#94A3B8' : ['ON_BREAK', 'PAUSED'].includes(live) ? '#F59E0B' : '#10B981'
  const p = data.punctuality
  // The live socket status wins: if the agent is anything but OFFLINE they
  // are logged in, whatever the last snapshot said.
  const loggedIn = live !== 'OFFLINE' || data.session.online

  return (
    <section className={`${CARD} overflow-hidden`}>
      <style>{LIVE_KEYFRAMES}</style>

      {/* Status ribbon */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-5 py-3">
        <div className="flex items-center gap-2.5">
          <LiveDot color={statusColor} />
          <span className="text-[13px] font-semibold text-slate-900">{statusLabel(live)}</span>
          {sinceIso && <span className="text-[12.5px] tabular-nums text-slate-500">for {clock(now - Date.parse(sinceIso))}</span>}
          {current && (
            <span className={`ml-1 inline-flex items-center gap-1 rounded-[3px] px-2 py-0.5 text-[11.5px] font-semibold ring-1 ring-inset ${inAdh ? 'bg-emerald-50 text-emerald-700 ring-emerald-200' : 'bg-rose-50 text-rose-700 ring-rose-200'}`}>
              {inAdh ? <CheckCircle2 className="h-3.5 w-3.5" /> : <TriangleAlert className="h-3.5 w-3.5" />}
              {inAdh ? 'In adherence' : 'Out of adherence'}
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2 text-[12px]">
          <span className="inline-flex items-center gap-1.5 rounded-[3px] bg-slate-50 px-2 py-1 text-slate-600 ring-1 ring-inset ring-slate-200/70">
            <LogIn className="h-3.5 w-3.5 text-slate-400" />
            {loggedIn && data.session.loginAt
              ? <>Logged in at {fmtTime(data.session.loginAt, tz)} · <b className="tabular-nums text-slate-800">{clock(now - Date.parse(data.session.loginAt))}</b></>
              : loggedIn ? <>Logged in</>
              : data.session.lastLogoutAt ? <>Logged out at {fmtTime(data.session.lastLogoutAt, tz)}</> : <>Logged out</>}
          </span>
          {p && !(loggedIn && p.status === 'not_logged_in') && <PunctualityPill p={p} tz={tz} />}
        </div>
      </div>

      <div className="grid gap-0 lg:grid-cols-[1.2fr_1.1fr_0.8fr]">
        {/* Now */}
        <div className="flex flex-col gap-3 p-5">
          <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-400">Right now</p>
          {current ? (
            <>
              <div className="flex items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[3px]" style={{ background: `${current.color ?? '#3366FF'}1A`, color: current.color ?? '#3366FF' }}>
                    <CatIcon className="h-5 w-5" />
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-[17px] font-semibold tracking-[-0.01em] text-slate-900">{current.activity}</p>
                    <p className="text-[12px] tabular-nums text-slate-500">{fmtTime(current.start, tz)} – {fmtTime(current.end, tz)}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-[24px] font-semibold leading-none tracking-[-0.02em] tabular-nums text-slate-900">{deferring ? clock(blockLength(current)) : clock(Date.parse(current.end) - now)}</p>
                  <p className="mt-1 text-[11px] text-slate-400">{deferring ? 'reserved — starts after your call' : 'remaining'}</p>
                </div>
              </div>
              <LiveBar pct={deferring ? 0 : blockPct} mode={deferring ? 'idle' : mode} />
              <div className="flex justify-between text-[11.5px] tabular-nums text-slate-400">
                <span>{clock(now - Date.parse(current.start))} elapsed</span>
                {next && <span>Next: <b className="font-semibold text-slate-600">{next.activity}</b> at {fmtTime(next.start, tz)}</span>}
              </div>
            </>
          ) : (
            <div className="flex items-center gap-3 rounded-[3px] bg-slate-50 px-4 py-4 ring-1 ring-inset ring-slate-200/70">
              <Clock3 className="h-5 w-5 text-slate-400" />
              <div>
                <p className="text-[14px] font-semibold text-slate-800">{next ? `${next.activity} starts in ${clock(Date.parse(next.start) - now)}` : 'Not scheduled right now'}</p>
                <p className="text-[12px] text-slate-500">
                  {next ? `at ${fmtTime(next.start, tz)}` : data.nextShift ? `Next shift ${new Date(data.nextShift.startAt).toLocaleString('en-US', { timeZone: tz, weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}` : 'No upcoming published shifts.'}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Shift */}
        <div className="flex flex-col gap-3 border-t border-slate-100 p-5 lg:border-l lg:border-t-0">
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-400">Today’s shift</p>
            {shiftStart && shiftEnd && (
              <span className={`rounded-[3px] px-1.5 py-0.5 text-[10.5px] font-bold uppercase tracking-wide ${now < shiftStart ? 'bg-slate-100 text-slate-500' : now >= shiftEnd ? 'bg-emerald-50 text-emerald-700' : 'bg-[#3366FF]/10 text-[#3366FF]'}`}>
                {now < shiftStart ? 'Upcoming' : now >= shiftEnd ? 'Finished' : 'In progress'}
              </span>
            )}
          </div>
          {data.shift && shiftStart && shiftEnd ? (
            <>
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-[17px] font-semibold tabular-nums tracking-[-0.01em] text-slate-900">{fmtTime(data.shift.start, tz)} – {fmtTime(data.shift.end, tz)}</span>
                <span className="text-right">
                  <span className="block text-[17px] font-semibold tabular-nums text-slate-900">
                    {now < shiftStart ? clock(shiftStart - now) : now >= shiftEnd ? '0:00' : clock(shiftEnd - now)}
                  </span>
                  <span className="block text-[11px] text-slate-400">{now < shiftStart ? 'until start' : 'left'}</span>
                </span>
              </div>
              <ShiftTrack blocks={blocks} start={shiftStart} end={shiftEnd} now={now} exceptions={data.today?.exceptions ?? []} tz={tz}
                late={p?.status === 'late' && p.at ? { until: Date.parse(p.at), label: `Late start · ${fmtSpan(p.minutes * 60_000)}` }
                  : p?.status === 'not_logged_in' && !loggedIn ? { until: now, label: 'Not logged in' } : null} />
              <p className="text-[12px] text-slate-500">
                <b className="tabular-nums text-slate-800">{fmtSpan(Math.max(0, Math.min(now, shiftEnd) - shiftStart))}</b> of {fmtSpan(shiftEnd - shiftStart)} done
                <span className="mx-1.5 text-slate-300">·</span>
                <b className="tabular-nums text-slate-800">{Math.max(0, Math.min(100, Math.round(shiftPct)))}%</b>
              </p>
            </>
          ) : <p className="text-[13px] text-slate-500">No shift scheduled today.</p>}
        </div>

        {/* Adherence */}
        <div className="flex min-w-[240px] flex-col gap-3 border-t border-slate-100 p-5 lg:border-l lg:border-t-0">
          <AdherenceMeter today={data.today} targetPct={data.targetPct} />
        </div>
      </div>

      {/* Suggestions */}
      <div className="flex flex-col gap-2 border-t border-slate-100 bg-slate-50/50 px-5 py-3.5">
        {tips.map((t) => (
          <div key={t.id} className={`flex items-start gap-2.5 rounded-[3px] px-3 py-2 text-[12.5px] ring-1 ring-inset ${TIP_CLS[t.tone]}`}>
            <t.Icon className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{t.text}</span>
          </div>
        ))}
      </div>
    </section>
  )
}

function PunctualityPill({ p, tz }: { p: NonNullable<MeAdherence['punctuality']>; tz: string }): JSX.Element {
  const map = {
    early: { cls: 'bg-sky-50 text-sky-700 ring-sky-200', text: `Logged in ${fmtSpan(p.minutes * 60_000)} before shift` },
    on_time: { cls: 'bg-emerald-50 text-emerald-700 ring-emerald-200', text: 'Started on time' },
    late: { cls: 'bg-rose-50 text-rose-700 ring-rose-200', text: `Started ${fmtSpan(p.minutes * 60_000)} late` },
    not_logged_in: { cls: 'bg-rose-50 text-rose-700 ring-rose-200', text: `Not logged in · shift started ${fmtSpan(p.minutes * 60_000)} ago` },
    upcoming: { cls: 'bg-slate-50 text-slate-600 ring-slate-200', text: `Shift starts in ${fmtSpan(p.minutes * 60_000)}` },
  }[p.status]
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-[3px] px-2 py-1 font-medium ring-1 ring-inset ${map.cls}`}>
      <AlarmClock className="h-3.5 w-3.5" />
      {map.text}{p.at ? <span className="font-normal opacity-75">· {fmtTime(p.at, tz)}</span> : null}
    </span>
  )
}

/**
 * The shift as one labelled track: activity segments (solid = done, faded =
 * still to come), red underline where you were off schedule, a "Now" flag,
 * start / end times and a legend.
 */
function ShiftTrack({ blocks, start, end, now, exceptions, tz, late }: { blocks: LiveBlock[]; start: number; end: number; now: number; exceptions: { start: string; end: string; expected: string; actual: string }[]; tz: string; late?: { until: number; label: string } | null }): JSX.Element {
  const span = Math.max(1, end - start)
  const pos = (ms: number): number => Math.min(100, Math.max(0, ((ms - start) / span) * 100))
  const inShift = blocks.filter((b) => Date.parse(b.end) > start && Date.parse(b.start) < end)
  const legend = [...new Map(inShift.map((b) => [b.activity, b.color ?? '#3366FF'])).entries()]
  const nowPct = pos(now)
  const live = now > start && now < end
  return (
    <div>
      <div className="relative pt-5">
        {live && (
          <span className="absolute top-0 -translate-x-1/2 rounded-[3px] bg-slate-900 px-1.5 py-px text-[10px] font-semibold text-white transition-[left] duration-1000 ease-linear" style={{ left: `${Math.min(94, Math.max(6, nowPct))}%` }}>
            Now
          </span>
        )}
        <div className="relative h-4 w-full overflow-hidden rounded-[3px] bg-slate-100">
          {inShift.map((b, i) => {
            const s = Date.parse(b.start)
            const e = Date.parse(b.end)
            const color = b.color ?? '#3366FF'
            // Solid up to now, faded after.
            const donePct = Math.max(0, Math.min(1, (now - s) / Math.max(1, e - s))) * 100
            return (
              <span key={i} title={`${b.activity} ${fmtTime(b.start, tz)} – ${fmtTime(b.end, tz)}`} className="absolute inset-y-0 border-r border-white"
                style={{ left: `${pos(s)}%`, width: `${pos(e) - pos(s)}%`, background: `linear-gradient(90deg, ${color} ${donePct}%, ${color}40 ${donePct}%)` }} />
            )
          })}
          {late && late.until > start && (
            <span title={late.label} className="absolute inset-y-0 left-0 border-r-2 border-rose-600"
              style={{ width: `${pos(late.until)}%`, background: 'repeating-linear-gradient(135deg,#FECDD3 0 4px,#FB7185 4px 7px)' }} />
          )}
          {exceptions.map((x, i) => (
            <span key={`x${i}`} title={`Off schedule: ${x.actual} during ${x.expected}`} className="absolute bottom-0 h-1"
              style={{ left: `${pos(Date.parse(x.start))}%`, width: `${Math.max(0.6, pos(Date.parse(x.end)) - pos(Date.parse(x.start)))}%`, background: '#E11D48' }} />
          ))}
          {live && <span className="absolute inset-y-0 w-[2px] bg-slate-900 transition-[left] duration-1000 ease-linear" style={{ left: `${nowPct}%` }} />}
        </div>
      </div>
      <div className="mt-1 flex justify-between text-[10.5px] tabular-nums text-slate-400">
        <span>{fmtTime(new Date(start).toISOString(), tz)}</span>
        <span>{fmtTime(new Date(end).toISOString(), tz)}</span>
      </div>
      <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-slate-500">
        {legend.map(([label, color]) => (
          <span key={label} className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-[2px]" style={{ background: color }} />{label}</span>
        ))}
        {late && late.until > start && (
          <span className="inline-flex items-center gap-1 font-semibold text-rose-600">
            <span className="h-2 w-3 rounded-[2px]" style={{ background: 'repeating-linear-gradient(135deg,#FECDD3 0 3px,#FB7185 3px 5px)' }} />{late.label}
          </span>
        )}
        {exceptions.length > 0 && <span className="inline-flex items-center gap-1"><span className="h-1 w-3 bg-rose-600" />Off schedule</span>}
      </div>
    </div>
  )
}

/** Adherence today in plain words: a meter with the target marked. */
function AdherenceMeter({ today, targetPct }: { today: MeAdherence['today']; targetPct: number }): JSX.Element {
  const pct = today?.adherencePct ?? null
  const scheduled = (today?.scheduledMin ?? 0) * 60_000
  const off = (today?.outOfAdherenceMin ?? 0) * 60_000
  const on = Math.max(0, scheduled - off)
  const tone = pct === null ? 'text-slate-400' : pct >= targetPct ? 'text-emerald-600' : pct >= targetPct - 10 ? 'text-amber-600' : 'text-rose-600'
  const bar = pct === null ? '#CBD5E1' : pct >= targetPct ? '#10B981' : pct >= targetPct - 10 ? '#F59E0B' : '#F43F5E'
  return (
    <>
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-400">Adherence today</p>
        <span className="text-[11px] text-slate-400">Target {targetPct}%</span>
      </div>
      <p className={`text-[30px] font-semibold leading-none tracking-[-0.02em] tabular-nums ${tone}`}>{pct === null ? '—' : `${Math.round(pct)}%`}</p>
      <div className="relative h-2.5 w-full rounded-[3px] bg-slate-100">
        <div className="h-full rounded-[3px] transition-[width] duration-700" style={{ width: `${Math.max(0, Math.min(100, pct ?? 0))}%`, background: bar }} />
        <span className="absolute -top-1 h-[18px] w-[2px] bg-slate-700" style={{ left: `${targetPct}%` }} title={`Target ${targetPct}%`} />
      </div>
      {pct === null ? (
        <p className="text-[12px] text-slate-500">Starts counting once your shift begins.</p>
      ) : (
        <div className="flex flex-col gap-1 text-[12px]">
          <span className="flex items-center justify-between"><span className="flex items-center gap-1.5 text-slate-600"><span className="h-2 w-2 rounded-full bg-emerald-500" />On schedule</span><b className="tabular-nums text-slate-800">{fmtSpan(on)}</b></span>
          <span className="flex items-center justify-between"><span className="flex items-center gap-1.5 text-slate-600"><span className="h-2 w-2 rounded-full bg-rose-500" />Off schedule</span><b className="tabular-nums text-slate-800">{fmtSpan(off)}</b></span>
          <span className="text-[11px] text-slate-400">of {fmtSpan(scheduled)} scheduled so far</span>
        </div>
      )}
    </>
  )
}
